import { vocab } from '@/ds';
import { formatNumber, formatTimestamp, toInstant } from '@/lib/format/format';
import type { Automation, Schedule, ScheduledSource } from './api';

// Automation and the schedule in the design system's words.

const plural = (n: number, noun: string) => `${formatNumber(n)} ${noun}${n === 1 ? '' : 's'}`;

const upperFirst = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

export function andList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
}

interface PauseRecord {
  pausedBy: string | null;
  pausedAt: string | null;
  pauseNote: string | null;
}

/** "Paused by ana · 25 Sep, 14:02 · “layout changed”". */
export function pauseLine(pause: PauseRecord, now = new Date()): string {
  return [
    pause.pausedBy ? `Paused by ${pause.pausedBy}` : 'Paused',
    pause.pausedAt ? formatTimestamp(pause.pausedAt, now) : null,
    pause.pauseNote ? `“${pause.pauseNote}”` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** What the pause dialogs' reason does, for help topics. */
export const PAUSE_NOTE_HELP =
  'The reason shows beside the switch and in the audit trail, and can’t be changed after the pause.';

/** The line as a sentence: a full stop, unless the note already ends one. */
export const asSentence = (line: string): string =>
  /[.!?]["”’)]?$/.test(line) ? line : `${line}.`;

/** Past its time and waiting for the next check; an overdue source is past that check too. */
export const isDue = (source: ScheduledSource, now = new Date()): boolean =>
  !source.overdue && toInstant(source.dueAt).getTime() <= now.getTime();

/** "retry 1 of 2". */
export const retryOf = (retry: { attempt: number; of: number }): string =>
  `${vocab.TRIGGER_RETRY} ${retry.attempt} ${vocab.TRIGGER_RETRY_OF} ${retry.of}`;

const withinCheck = (schedule: Schedule) =>
  `within ${plural(schedule.checkIntervalMinutes, 'minute')}`;

const overdueWords = (source: ScheduledSource, now: Date) =>
  `${vocab.HEALTH.overdue?.label}: due since ${formatTimestamp(source.dueAt, now)}, nothing ran`;

const retryWords = (
  source: ScheduledSource,
  retry: NonNullable<ScheduledSource['retry']>,
  schedule: Schedule,
  now: Date,
) =>
  `${upperFirst(retryOf(retry))}: ${
    isDue(source, now)
      ? `due now, starts ${withinCheck(schedule)}`
      : formatTimestamp(source.dueAt, now)
  }`;

/** An entry's next run: overdue wins over a retry, which wins over plain due. */
export function dueWhen(source: ScheduledSource, schedule: Schedule, now = new Date()): string {
  if (source.overdue) return overdueWords(source, now);
  if (source.retry) return retryWords(source, source.retry, schedule, now);
  return isDue(source, now)
    ? `Due now: starts ${withinCheck(schedule)}`
    : formatTimestamp(source.dueAt, now);
}

const AUTOMATION_PAUSED = 'automation is paused';
const SCHEDULE_OFF = 'the schedule is off in this environment';

/** Why the schedule starts nothing at all, if it doesn't. */
const holdOf = (automation: Automation): string | null =>
  automation.paused ? AUTOMATION_PAUSED : !automation.schedule.enabled ? SCHEDULE_OFF : null;

const overdueSentence = (overdue: ScheduledSource[]) =>
  `${overdue.length === 1 ? `${overdue[0]?.name} is` : `${plural(overdue.length, 'source')} are`} overdue: the schedule may have stopped.`;

/** What the schedule does next. */
export function nextRunSentence(automation: Automation, now = new Date()): string {
  const { schedule } = automation;
  const hold = holdOf(automation);
  if (hold === AUTOMATION_PAUSED) return `${upperFirst(hold)}, so nothing runs on a schedule.`;
  if (hold) return `Runs start only by hand: ${hold}.`;
  const [next] = schedule.sources;
  if (!next) return 'Nothing is scheduled: no source is enabled and unpaused.';
  const overdue = schedule.sources.filter((s) => s.overdue);
  const due = schedule.sources.filter((s) => isDue(s, now));
  return [overdue.length ? overdueSentence(overdue) : null, upNextSentence(schedule, next, due, overdue, now)]
    .filter(Boolean)
    .join(' ');
}

function upNextSentence(
  schedule: Schedule,
  next: ScheduledSource,
  due: ScheduledSource[],
  overdue: ScheduledSource[],
  now: Date,
): string | null {
  if (due.length) return `The schedule starts ${andList(due.map((s) => s.name))} ${withinCheck(schedule)}.`;
  if (overdue.length) return null;
  const retry = next.retry ? `'s ${retryOf(next.retry)}` : '';
  return `The next scheduled run is ${next.name}${retry}, ${formatTimestamp(next.dueAt, now)}.`;
}

interface ScheduleCandidate {
  id: string;
  enabled: boolean;
  paused: boolean;
}

/** A source's next scheduled run, or why it has none. The schedule lists only enabled, unpaused sources (the API's `ScrapeAutomationRequests`). */
export function nextRunLine(
  source: ScheduleCandidate,
  automation: Automation,
  running: boolean,
  now = new Date(),
): string {
  if (running) return 'Running now';
  const reason = !source.enabled
    ? 'the source is disabled'
    : source.paused
      ? 'the source is paused'
      : holdOf(automation);
  if (reason) return `Not scheduled: ${reason}`;
  const entry = automation.schedule.sources.find((s) => s.sourceId === source.id);
  return entry ? dueWhen(entry, automation.schedule, now) : 'Not available';
}
